import { z } from 'zod';

/**
 * The `bb` push payload v1 (BB-72, PUSH-007): what the Android and iOS SDKs receive and recognize as ours. Android
 * gets it as the FCM data entry `bb` (a JSON string) and shows the notification itself; iOS gets it as the
 * dictionary `userInfo.bb` next to `aps`, which carries the text. Unknown fields are kept here and ignored by the
 * SDKs (baseline §9); a breaking change bumps `v`, and an SDK that does not know the version reports
 * `unsupported_version` instead of showing it.
 */
export const PUSH_PAYLOAD_VERSION = 1;

/** FCM data and APNs payload limit after serialization; a bigger push fails before the provider (`payload_too_large`). */
export const PUSH_PAYLOAD_MAX_BYTES = 4096;

export const PUSH_SYNC_HINTS = ['cards', 'iam'] as const;
export type PushSyncHint = (typeof PUSH_SYNC_HINTS)[number];

const HttpsUrl = z.url({ protocol: /^https$/, error: 'must be an https:// URL' }).max(2048);
const DeepLink = z
  .string()
  .min(1)
  .max(2048)
  .regex(/^[a-z][a-z0-9+.-]*:/i, 'must be a URI with a scheme');

/** What a tap on the push body does; the same `kind` discriminator and URI rules as `CardAction` (BB-61). */
export const PushAction = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('open_app') }),
  z.strictObject({ kind: z.literal('deep_link'), uri: DeepLink }),
  z.strictObject({ kind: z.literal('web_url'), uri: HttpsUrl, openIn: z.enum(['in_app', 'external']) }),
]);
export type PushAction = z.infer<typeof PushAction>;

export const PushButtonId = z.string().regex(/^[a-z0-9_]{1,32}$/, 'lowercase letters, digits and _ (up to 32)');

/**
 * An action button (BB-74). Android draws `label`; on iOS the titles come from the notification category the app
 * registered (`cat`), so iOS payloads carry the button actions without labels. `dismiss` closes the notification and
 * is not an open.
 */
export const PushButton = z.strictObject({
  id: PushButtonId,
  label: z.string().min(1).max(64).optional(),
  act: z.discriminatedUnion('kind', [...PushAction.options, z.strictObject({ kind: z.literal('dismiss') })]),
});
export type PushButton = z.infer<typeof PushButton>;

const ExtraKey = z.string().min(1).max(100);
export const PushExtraValue = z.union([z.string().max(1000), z.number(), z.boolean()]);

export const BbPushPayloadV1 = z.looseObject({
  v: z.literal(PUSH_PAYLOAD_VERSION),
  /** `push_delivery.id`: dedupe on the device, receipt and open reports (`push_interaction.deliveryId`). */
  did: z.uuid(),
  /** Message API id (campaign or Canvas step) and variant API id, for the host's analytics. */
  mid: z.string().min(1).max(120).optional(),
  vid: z.string().min(1).max(120).optional(),
  /** Installation identity epoch at send time (DEC-008): an SDK with a newer epoch drops it (`identity_changed`). */
  ep: z.int().min(0).optional(),
  kind: z.enum(['visible', 'silent']),
  /** Android text: title, body, summary (iOS text lives in `aps.alert`). */
  t: z.string().optional(),
  b: z.string().optional(),
  sum: z.string().optional(),
  /** Android notification channel id. */
  ch: z.string().min(1).max(64).optional(),
  /** Android notification id: a push with the same id replaces the shown one. */
  nid: z.int32().optional(),
  img: HttpsUrl.optional(),
  /** Android large icon. */
  icon: HttpsUrl.optional(),
  /** Absent — open the app (left optional rather than defaulted so the published JSON Schema does not require it). */
  act: PushAction.optional(),
  btn: z.array(PushButton).max(3).optional(),
  /** iOS notification category (the same as `aps.category`). */
  cat: z.string().min(1).max(64).optional(),
  ex: z.record(ExtraKey, PushExtraValue).optional(),
  /** The SDK does not show the push after this moment (`expired`). */
  exp: z.iso.datetime({ offset: true }).optional(),
  sentAt: z.iso.datetime({ offset: true }),
  /** Silent hint: refresh these feeds now (PUSH-008). */
  sync: z.array(z.enum(PUSH_SYNC_HINTS)).min(1).max(PUSH_SYNC_HINTS.length).optional(),
});
export type BbPushPayloadV1 = z.infer<typeof BbPushPayloadV1>;

/**
 * The silent uninstall probe (BB-158): `bb` = `{ "t": "probe" }` with no `v`, `did` or `kind`, in an FCM data-only
 * message (NORMAL priority) or an APNs background push (`content-available`, priority 5). The service only reads the
 * provider's answer (`UNREGISTERED` / 410 → uninstall). An SDK recognizes a `bb` without `v` whose `t` is `probe`,
 * shows nothing and reports nothing; a v1 payload titled "probe" is an ordinary push.
 */
export const BbProbePayload = z.strictObject({ t: z.literal('probe') });
export type BbProbePayload = z.infer<typeof BbProbePayload>;
export const PUSH_PROBE_PAYLOAD: BbProbePayload = { t: 'probe' };
